import { zodResolver } from "@hookform/resolvers/zod";
import { makeStyles, Box, Typography } from "@material-ui/core";
import dayjs from "dayjs";
import * as React from "react";
import { useForm, Controller } from "react-hook-form";
import { useSetRecoilState } from "recoil";

import { CustomButton } from "@/components/Button";
import { PropertyEditErrorSummary } from "@/components/property_edit/PropertyEditErrorSummary";
import { CustomForm } from "@/components/property_edit/PropertyEditInputs";
import { DatePickerComponent } from "@/components/property_edit/PropertyEditInputs";
import { PropertyEditLayout as BaseTemplate } from "@/components/property_edit/PropertyEditLayout";
import { PropallyApiFactory } from "@/module/CustomApiFactory";
import { parseFormattedNumber } from "@/utilities/format";
import { editPropertyPurchaseInfoSchema, EditPropertyPurchaseInfoFormData } from "@/validations/management/edit";
import { PropertyHolder, PropertyManagementDetailSnackbarState, useRequestInitialLoading } from "@/view_models/atoms";

import { loanError } from "../../utils/loanError";
import { LoanConditionsDraft, purchaseLoanHistory, hasApplicableLoanRate } from "../../utils/purchaseLoanDraft";

import { LoanConditionsRepair } from "./loan_info/LoanConditionsRepair";

const useStyles = makeStyles(() => ({
  content: {
    maxWidth: 640,
    margin: "0 auto",
  },
}));

interface EditPropertySectionPurchaseInfoProps {
  propertyHolder: PropertyHolder;
  onClickPageBack: () => void;
}

export const EditPropertySectionPurchaseInfo: React.FC<EditPropertySectionPurchaseInfoProps> = ({
  propertyHolder,
  onClickPageBack,
}) => {
  const classes = useStyles();
  const [saveError, setSaveError] = React.useState<{ message: string; reason?: string }>();
  const [saving, setSaving] = React.useState(false);
  const [loanDraft, setLoanDraft] = React.useState<LoanConditionsDraft>();
  const pending = React.useRef(false);
  const {
    control,
    formState: { errors },
    watch,
    setError,
    clearErrors,
    handleSubmit,
  } = useForm<EditPropertyPurchaseInfoFormData>({
    resolver: zodResolver(editPropertyPurchaseInfoSchema),
    defaultValues: {
      bought_at: propertyHolder.property.bought_at
        ? dayjs(propertyHolder.property.bought_at).format("YYYY/MM/DD")
        : dayjs(new Date()).format("YYYY/MM/DD"),
      total_price_of_property: propertyHolder.property.total_price_of_property
        ? propertyHolder.property.total_price_of_property.toString()
        : "",
      priceof_tax: propertyHolder.property.priceof_tax ? propertyHolder.property.priceof_tax.toString() : "",
      priceof_building: propertyHolder.property.priceof_building
        ? propertyHolder.property.priceof_building.toString()
        : "",
      priceof_land: propertyHolder.property.priceof_land ? propertyHolder.property.priceof_land.toString() : "",
      priceof_facilities: propertyHolder.property.priceof_facilities
        ? propertyHolder.property.priceof_facilities.toString()
        : "",
      property_acquisition_tax: propertyHolder.property.property_acquisition_tax
        ? propertyHolder.property.property_acquisition_tax.toString()
        : "",
      property_acquisition_tax_payment_month: propertyHolder.property.property_acquisition_tax_payment_month
        ? dayjs(propertyHolder.property.property_acquisition_tax_payment_month).format("YYYY/MM")
        : null,
      misc_expences_at_bought: propertyHolder.property.misc_expences_at_bought
        ? propertyHolder.property.misc_expences_at_bought.toString()
        : "",
    },
  });
  const requestInitialLoading = useRequestInitialLoading();
  // 物件詳細でのスナックバー表示
  const setManagementDetailSnackBarState = useSetRecoilState(PropertyManagementDetailSnackbarState);

  const watchValues = watch();

  const propertyInfo = React.useMemo(() => {
    return {
      bought_at:
        typeof watchValues.bought_at === "string"
          ? watchValues.bought_at
          : dayjs(watchValues.bought_at).format("YYYY/MM/DD"),
      total_price_of_property: parseFormattedNumber(watchValues.total_price_of_property),
      priceof_tax: parseFormattedNumber(watchValues.priceof_tax),
      priceof_building: parseFormattedNumber(watchValues.priceof_building),
      priceof_land: parseFormattedNumber(watchValues.priceof_land),
      priceof_facilities: parseFormattedNumber(watchValues.priceof_facilities),
      property_acquisition_tax: parseFormattedNumber(watchValues.property_acquisition_tax),
      property_acquisition_tax_payment_month: watchValues.property_acquisition_tax_payment_month
        ? typeof watchValues.property_acquisition_tax_payment_month === "string"
          ? watchValues.property_acquisition_tax_payment_month
          : dayjs(watchValues.property_acquisition_tax_payment_month).format("YYYY/MM")
        : null,
      misc_expences_at_bought: parseFormattedNumber(watchValues.misc_expences_at_bought),
    };
  }, [watchValues]);

  const fp = PropallyApiFactory();
  const onSubmit = async () => {
    if (pending.current) return;
    setSaveError(undefined);
    // --- クライアント側バリデーション（PATCH用） ---
    clearErrors();
    const total = propertyInfo.total_price_of_property;
    const parts = [
      propertyInfo.priceof_tax,
      propertyInfo.priceof_building,
      propertyInfo.priceof_land,
      propertyInfo.priceof_facilities,
    ].map((v) => (typeof v === "number" ? v : 0));
    let hasError = false;
    // 1. 購入価格は1以上の整数
    if (!(Number.isInteger(total) && total > 0)) {
      setError("total_price_of_property", { type: "manual", message: "1以上の整数を入力してください" });
      hasError = true;
    }
    // 2. 価格内訳の整合性（いずれか>0のとき）と負数禁止（各項目で表示）
    if (
      [
        propertyInfo.priceof_tax,
        propertyInfo.priceof_building,
        propertyInfo.priceof_land,
        propertyInfo.priceof_facilities,
      ].some((n) => n < 0)
    ) {
      if ((propertyInfo.priceof_tax ?? 0) < 0)
        setError("priceof_tax", { type: "manual", message: "0以上の整数を入力してください" });
      if ((propertyInfo.priceof_building ?? 0) < 0)
        setError("priceof_building", { type: "manual", message: "0以上の整数を入力してください" });
      if ((propertyInfo.priceof_land ?? 0) < 0)
        setError("priceof_land", { type: "manual", message: "0以上の整数を入力してください" });
      if ((propertyInfo.priceof_facilities ?? 0) < 0)
        setError("priceof_facilities", { type: "manual", message: "0以上の整数を入力してください" });
      hasError = true;
    }
    const sum = parts.reduce((a, b) => a + b, 0);
    if (Math.abs(sum - total) > 5) {
      setError("total_price_of_property", {
        type: "manual",
        message: "購入価格が内訳合計と一致しません（うち消費税+うち建物代+うち土地代+建物付属設備代（税抜）の合計）",
      });
      hasError = true;
    }
    if (hasError) return;

    pending.current = true;
    setSaving(true);
    try {
      let loanInfo = loanDraft;
      if (loanDraft) {
        const history = purchaseLoanHistory(
          propertyHolder.user_input_monthly_cashflow_results,
          propertyHolder.property.bought_at,
          propertyInfo.bought_at
        );
        // 下書きの確定後に購入月を再変更した場合も、不要になった初期金利を追加しない。
        if (loanDraft.loan_amount === 0 || hasApplicableLoanRate(history, loanDraft.payment_starts_at)) {
          loanInfo = { ...loanDraft };
          delete loanInfo.initial_loan_interest_rate_percentage;
        }
      }
      const data = await fp.v1PropertiesPropertyIdPatch(
        propertyHolder.property.id,
        { ...propertyInfo, ...loanInfo },
        {
          withCredentials: true,
        }
      );
      if (data.status === 200) {
        setManagementDetailSnackBarState({
          message: "物件情報を変更しました",
          status: "success",
          isOpen: true,
        });
        requestInitialLoading();
        onClickPageBack();
      }
    } catch (e) {
      const failure = loanError(e);
      const message =
        typeof e.response?.data?.message === "string" &&
        e.response.data.message.includes("invalid_total_price_of_property")
          ? "「購入価格 = うち消費税 + うち建物代 + うち土地代 + 建物付属設備代」となっていません"
          : failure.message;
      setSaveError({ ...failure, message });
    } finally {
      pending.current = false;
      setSaving(false);
    }
  };

  return (
    <BaseTemplate headerTitle="購入時情報編集" onBack={onClickPageBack}>
      <Box>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Box className={classes.content}>
            {/* 購入時期 */}
            <Typography data-edit-section component="h3">
              購入
            </Typography>
            <Controller
              name="bought_at"
              control={control}
              render={({ ...field }) => <DatePickerComponent label="購入時期" {...field} />}
            />
            {/* 購入価格 */}
            <Controller
              name="total_price_of_property"
              control={control}
              render={({ ...field }) => (
                <CustomForm
                  {...field}
                  label="購入価格（税込）"
                  required
                  error={!!errors.total_price_of_property}
                  errorText={errors.total_price_of_property?.message}
                  placeHolder="100,000,000"
                  unitSuffix="円"
                  formatNumeric
                />
              )}
            />
            {/* うち消費税 */}
            <Typography data-edit-section component="h3">
              価格の内訳
            </Typography>
            <Controller
              name="priceof_tax"
              control={control}
              render={({ ...field }) => (
                <CustomForm
                  {...field}
                  label="うち消費税"
                  required
                  error={!!errors.priceof_tax}
                  errorText={errors.priceof_tax?.message}
                  placeHolder="100,000,000"
                  unitSuffix="円"
                  isShort
                  formatNumeric
                />
              )}
            />
            {/* うち建物代 */}
            <Controller
              name="priceof_building"
              control={control}
              render={({ ...field }) => (
                <CustomForm
                  {...field}
                  label="うち建物代"
                  required
                  error={!!errors.priceof_building}
                  errorText={errors.priceof_building?.message}
                  placeHolder="100,000,000"
                  unitSuffix="円"
                  formatNumeric
                />
              )}
            />

            {/* うち土地代 */}
            <Controller
              name="priceof_land"
              control={control}
              render={({ ...field }) => (
                <CustomForm
                  {...field}
                  label="うち土地代"
                  required
                  error={!!errors.priceof_land}
                  errorText={errors.priceof_land?.message}
                  placeHolder="100,000,000"
                  unitSuffix="円"
                  formatNumeric
                />
              )}
            />

            {/* 建物付属設備代（税抜） */}
            <Controller
              name="priceof_facilities"
              control={control}
              render={({ ...field }) => (
                <CustomForm
                  {...field}
                  label="建物付属設備代（税抜）"
                  error={!!errors.priceof_facilities}
                  errorText={errors.priceof_facilities?.message}
                  placeHolder="100,000,000"
                  unitSuffix="円"
                  formatNumeric
                />
              )}
            />
            {/* 購入時不動産取得税 */}
            <Typography data-edit-section component="h3">
              購入時の費用
            </Typography>
            <Controller
              name="property_acquisition_tax"
              control={control}
              render={({ ...field }) => (
                <CustomForm
                  {...field}
                  label="購入時不動産取得税"
                  error={!!errors.property_acquisition_tax}
                  errorText={errors.property_acquisition_tax?.message}
                  placeHolder="100,000,000"
                  unitSuffix="円"
                  formatNumeric
                />
              )}
            />
            {/* 不動産取得税の支払い月 */}
            <Controller
              name="property_acquisition_tax_payment_month"
              control={control}
              render={({ ...field }) => <DatePickerComponent label="不動産取得税の支払い月" {...field} />}
            />
            {/* 購入時その他諸費用  */}
            <Controller
              name="misc_expences_at_bought"
              control={control}
              render={({ ...field }) => (
                <CustomForm
                  {...field}
                  label="購入時その他諸費用"
                  error={!!errors.misc_expences_at_bought}
                  errorText={errors.misc_expences_at_bought?.message}
                  placeHolder="100,000,000"
                  unitSuffix="円"
                  formatNumeric
                />
              )}
            />
          </Box>
          {saveError && (
            <Typography role="alert" color="error">
              {saveError.message}
            </Typography>
          )}
          {loanDraft && (
            <Box mt={2}>
              <Typography role="status" variant="body2">
                借入条件の変更は未保存です。ローン借入金額 {loanDraft.loan_amount.toLocaleString()}円、 返済年数{" "}
                {loanDraft.payment_period}年、初回返済月 {dayjs(loanDraft.payment_starts_at).format("YYYY/MM")}。
                「変更を保存」で購入情報と一緒に保存します。
              </Typography>
              <CustomButton
                type="button"
                customVariant="textPrimary"
                disabled={saving}
                onClick={() => setLoanDraft(undefined)}
              >
                借入条件の変更を取り消す
              </CustomButton>
            </Box>
          )}
          <PropertyEditErrorSummary errors={errors} />
          <Box mt={2}>
            <CustomButton customVariant="filled" buttonSize="L" size="small" type="submit" fullWidth disabled={saving}>
              変更を保存
            </CustomButton>
          </Box>
        </form>
        <LoanConditionsRepair
          reason={saveError?.reason}
          pendingBoughtAt={propertyInfo.bought_at}
          draft={loanDraft}
          onDraft={setLoanDraft}
          disabled={saving}
          onRepaired={() => setSaveError(undefined)}
        />
      </Box>
    </BaseTemplate>
  );
};
